import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { useLocalStorageValue } from "@react-hookz/web";
import { useEffect, type FC } from "react";
import { useDialog } from "../../../../../../../utils/hooks/use-dialog";
import type { UUID } from "../../../../../../../utils/uuid";

export const ReviewDialog: FC<{ momentId: UUID }> = ({ momentId }) => {
  const key = `comm-review-message-${momentId}`;

  const { set: setShowReviewInfoMessage, value: showReviewInfoMessage } = useLocalStorageValue(
    key,
    { defaultValue: true },
  );

  const { handleOpen, handleClose, open } = useDialog();

  useEffect(() => {
    if (showReviewInfoMessage) handleOpen();
  }, [showReviewInfoMessage, handleOpen]);

  if (!showReviewInfoMessage) return null;

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Review your Communication before Publishing</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Communications can’t be edited or deleted after they have been sent.
        </Typography>

        <List sx={{ pt: 0 }}>
          {[
            "Check the channel, schedule, and recipients to make sure they’re correct.",
            "Review the content of your Communication. If it contains Dynamic Content or Merge Tags, you can preview as specific recipients to see what they will see.",
            "Bear in mind that layout and appearance may differ depending on the recipient’s device and settings.",
          ].map((text, index) => (
            <ListItem key={index} sx={{ alignItems: "flex-start", px: 0, py: 0.5 }}>
              <ListItemText
                primary={text}
                slotProps={{
                  primary: {
                    variant: "body2",
                    fontWeight: index === 2 ? "bold" : "normal",
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => {
            setShowReviewInfoMessage(false);
            handleClose();
          }}
          variant="text"
          color="primary"
          data-analytics-id="review-dialog-understand"
        >
          I understand
        </Button>
      </DialogActions>
    </Dialog>
  );
};
